/**
 * ============================================================================
 * GRATIA - assets/carousel.js
 * ----------------------------------------------------------------------------
 * <gratia-carousel> - a lightweight, dependency-free, scroll-snap slider used
 * by product / collection / promo / review sections. Native scroll-snap does
 * the heavy lifting (buttery on touch); JS adds arrows, dots, drag, autoplay,
 * and a11y. Degrades to a horizontally scrollable list without JS.
 *
 * Markup contract:
 *   <gratia-carousel data-autoplay="0" data-loop="false">
 *     <div data-carousel-track> <div data-carousel-slide>…</div> … </div>
 *     <button data-carousel-prev></button>
 *     <button data-carousel-next></button>
 *     <div data-carousel-dots></div>   (optional)
 *   </gratia-carousel>
 * ============================================================================
 */
(function () {
  'use strict';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class GratiaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
      // Arrows may live OUTSIDE the element (e.g. in the section's title row),
      // so fall back to searching the surrounding section.
      const scope = this.closest('.shopify-section') || this.closest('section') || this;
      this.prevBtn = this.querySelector('[data-carousel-prev]') || scope.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]') || scope.querySelector('[data-carousel-next]');
      this.dotsWrap = this.querySelector('[data-carousel-dots]') || scope.querySelector('[data-carousel-dots]');
      this.autoplay = parseInt(this.dataset.autoplay || '0', 10);
      this.index = 0;

      this.prevBtn?.addEventListener('click', () => this.scrollByPage(-1));
      this.nextBtn?.addEventListener('click', () => this.scrollByPage(1));

      this.onScroll = Gratia.utils?.rafThrottle
        ? Gratia.utils.rafThrottle(() => this.updateState())
        : () => this.updateState();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.buildDots();
      this.enableDrag();
      this.updateState();

      if (this.autoplay > 0 && !reduceMotion && this.slides.length > 1) {
        // Pause on hover/focus (WCAG 2.2.2). Bound once here — NOT inside
        // startAuto (which runs on every mouseleave), else listeners stack up.
        this.addEventListener('mouseenter', () => this.stopAuto());
        this.addEventListener('mouseleave', () => this.startAuto());
        this.addEventListener('focusin', () => this.stopAuto());
        this.addEventListener('focusout', () => this.startAuto());
        this.startAuto();
      }

      window.addEventListener('resize', () => this.updateState(), { passive: true });
    }

    get pageWidth() {
      // width of one slide incl. gap
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    slidesPerView() {
      return Math.max(1, Math.round(this.track.clientWidth / this.pageWidth));
    }

    scrollByPage(dir) {
      const spv = this.slidesPerView();
      this.track.scrollBy({ left: dir * this.pageWidth * spv, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.pauseAuto();
    }

    scrollToIndex(i) {
      const target = this.slides[i];
      if (target) {
        this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    updateState() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      const loop = this.dataset.loop === 'true';

      if (this.prevBtn) {
        this.prevBtn.disabled = atStart && !loop;
        this.prevBtn.classList.toggle('opacity-40', atStart && !loop);
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = atEnd && !loop;
        this.nextBtn.classList.toggle('opacity-40', atEnd && !loop);
      }

      this.index = Math.round(scrollLeft / this.pageWidth);
      this.dots?.forEach((d, i) => {
        const active = i === this.index;
        d.setAttribute('aria-current', active ? 'true' : 'false');
        d.classList.toggle('is-active', active);
      });
    }

    buildDots() {
      if (!this.dotsWrap || this.slides.length < 2) return;
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel-dot';
        dot.setAttribute('aria-label', `${i + 1}`);
        dot.addEventListener('click', () => {
          this.scrollToIndex(i);
          this.pauseAuto();
        });
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    enableDrag() {
      let down = false, startX = 0, startScroll = 0, moved = false;
      const start = (x) => { down = true; moved = false; startX = x; startScroll = this.track.scrollLeft; this.track.classList.add('is-dragging'); };
      const move = (x) => { if (!down) return; const dx = x - startX; if (Math.abs(dx) > 4) moved = true; this.track.scrollLeft = startScroll - dx; };
      const end = () => { down = false; this.track.classList.remove('is-dragging'); };

      this.track.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse') start(e.clientX); });
      this.track.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') move(e.clientX); });
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointerleave', end);
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); } }, true);
    }

    startAuto() {
      if (this.autoplay <= 0 || reduceMotion || this.slides.length < 2) return;
      this.stopAuto();
      this.timer = setInterval(() => {
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
        if (atEnd) this.scrollToIndex(0);
        else this.scrollByPage(1);
      }, this.autoplay * 1000);
    }

    stopAuto() { clearInterval(this.timer); }
    pauseAuto() { this.stopAuto(); if (this.autoplay > 0 && !reduceMotion) setTimeout(() => this.startAuto(), 6000); }
    disconnectedCallback() { this.stopAuto(); }
  }

  const Gratia = (window.Gratia = window.Gratia || {});
  if (!customElements.get('gratia-carousel')) customElements.define('gratia-carousel', GratiaCarousel);
})();
